import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CURRENCY, STATUS } from 'src/constants/common';

import { InitialState } from '../app/MyQuote/QuoteDetail/quote.interface';
import { addActiveQuoteTag, sortDataByIsQuoted } from '../app/MyQuote/quotingDashboard.helpers';

import { RootState } from './store';

const initialState: InitialState = {
    quote: {},
    dimensions: [],
    addressDetails: {},
    loadLinkPosts: [],
    services: [],
    isShowQuoteDetailModal: false,
    activeTab: 'address',
    isViewQuote: true,
    activeQuotes: [],
    commonCarrierRates: [],
    spotQuoteRates: [],
    currencyType: CURRENCY.CAD,
    ratesTableCurrency: CURRENCY.CAD,
    loadPostingTableCurrency: CURRENCY.CAD,
    isStatusWonOrLost: false,
    isOpenPostToLoadBoard: false,
    loadPostingDetail: {},
    isViewLoadPost: false,
    customerOverview: {
        avgWinLoss: 0,
        customerName: '',
        customerStrategy: null,
        customerTags: '',
        laneWinLoss: 0,
        negotiation: '',
        isStrategyView: false,
        customerStatus: null,
    },
    predictiveIndex: {},
    isRedirectedFromRateGatherAgent: false,
    quoteConnectedUsers: [],
    routeDistance: 0,
    isQuoteDetailsUpdated: false,
    isShowLinkThreadModal: false,
    showTranscriptPanel: false,
    selectedLeadData: null,
    commonCarriers: [],
    selectedCommonCarrierData: null,
    isShowCommonCarriersOverviewModal: false,
    isLoading: true,
    selectedRateLoadPosting: null,
    rateData: [],
    rateCount: 0,
    isRatesLoading: false,
    isSpotQuoteLoadingWhenEditQuoteDetail: false,
    activeQuoteColumns: null,
    activeQuotesFetchedForId: null,
    activities: [],
    activitiesPagination: null,
    activitiesLoading: false,
    activitiesPostLoading: false,
};

export const QuotingSlice = createSlice({
    name: 'quoting',
    initialState,
    reducers: {
        resetQuoteState: (state) => ({
            ...initialState,
            activeQuotes: state.activeQuotes,
            activeQuoteColumns: state.activeQuoteColumns,
            activeQuotesFetchedForId: state.activeQuotesFetchedForId,
            activities: state.activities,
            activitiesPagination: state.activitiesPagination,
            activitiesLoading: state.activitiesLoading,
            activitiesPostLoading: state.activitiesPostLoading,
        }),
        resetAllQuoteState: () => initialState,
        setQuote: (state, { payload }) => {
            state.quote = payload;
        },

        setDimensions: (state, { payload }) => {
            state.dimensions = payload;
        },

        setAddressDetails: (state, { payload }) => {
            state.addressDetails = {
                ...state.addressDetails,
                ...payload,
            };
        },

        setIsSpotQuoteLoadingWhenEditQuoteDetail: (state, action) => {
            state.isSpotQuoteLoadingWhenEditQuoteDetail = action.payload;
        },

        setLoadLinkPosts: (state, { payload }) => {
            state.loadLinkPosts = payload;
        },

        setServices: (state, { payload }) => {
            state.services = payload;
        },

        setIsShowQuoteDetailModal: (state, { payload }) => {
            state.isShowQuoteDetailModal = payload;
        },

        setActiveTabQuote: (state, { payload }) => {
            state.activeTab = payload;
        },

        setIsViewQuote: (state, { payload }) => {
            state.isViewQuote = payload;
        },

        setActiveQuotes: (state, action: PayloadAction<any[]>) => {
            state.activeQuotes = action.payload;
            sessionStorage.setItem(`activeCarrierCount_${state.addressDetails.id}`, state.activeQuotes.length.toString());
        },

        updateRemoveActiveQuote: (
            state,
            action: PayloadAction<{
                activeQuotes: any[];
                isRemove?: boolean;
            }>
        ) => {
            const { activeQuotes, isRemove } = action.payload;

            const activeQuoteIds = activeQuotes.map((quote) => quote.id);

            if (isRemove) {
                state.activeQuotes = state.activeQuotes.filter((quote) => !activeQuoteIds.includes(quote.id));

                if (state.activeQuotes.length === 0) {
                    state.addressDetails.status = STATUS.OPENED;
                } else {
                    state.activeQuotes = sortDataByIsQuoted(addActiveQuoteTag(state.activeQuotes));
                }

                activeQuoteIds.forEach((id) => {
                    if (state.addressDetails?.toBookQuoteCarrierId == id) {
                        state.addressDetails.toBookQuoteCarrierId = null;

                        state.addressDetails.status = state.activeQuotes.length > 0 ? STATUS.ACTIVE : STATUS.OPENED;
                    }
                });
            } else {
                // UPDATE ACTIVE QUOTES
                const updatedActiveQuotes = state.activeQuotes.map((quote) => {
                    if (activeQuoteIds.includes(quote.id)) {
                        return activeQuotes.find((q) => q.id === quote.id);
                    }

                    return quote;
                });

                // ADD ACTIVE QUOTES
                const newActiveQuotes = activeQuotes.filter((quote) => !state.activeQuotes.some((q) => q.id === quote.id));

                state.activeQuotes = sortDataByIsQuoted(addActiveQuoteTag([...updatedActiveQuotes, ...newActiveQuotes]));
            }

            sessionStorage.setItem(`activeCarrierCount_${state.addressDetails.id}`, state.activeQuotes.length.toString());
        },

        updateActiveQuoteValue: (state, { payload }) => {
            state.activeQuotes = state.activeQuotes.map((quote) => {
                if (quote.id === payload.id) {
                    return { ...quote, ...payload };
                }

                return quote;
            });
        },

        setCurrencyType: (state, { payload }) => {
            state.currencyType = payload;
        },

        setRatesTableCurrency: (state, { payload }) => {
            state.ratesTableCurrency = payload;
        },

        setLoadPostingTableCurrency: (state, { payload }) => {
            state.loadPostingTableCurrency = payload;
        },

        setIsStatusWonOrLost: (state, { payload }) => {
            state.isStatusWonOrLost = payload;
        },

        setIsOpenPostToLoadBoard: (state, { payload }) => {
            state.isOpenPostToLoadBoard = payload;
        },

        setLoadPostingDetail: (state, { payload }) => {
            state.loadPostingDetail = payload;
        },

        setCustomerOverview: (state, { payload }) => {
            state.customerOverview = { ...state.customerOverview, ...payload };
        },

        setIsRedirectedFromRateGatherAgent: (state, { payload }) => {
            state.isRedirectedFromRateGatherAgent = payload;
        },

        setCommonCarrierRates: (state, { payload }) => {
            state.commonCarrierRates = payload;
        },

        setSpotQuoteRates: (state, { payload }) => {
            state.spotQuoteRates = payload;
        },

        setQuoteConnectedUsers: (state, { payload }) => {
            state.quoteConnectedUsers = payload;
        },

        setPredictiveIndex: (state, { payload }) => {
            state.predictiveIndex = payload;
        },

        setRouteDistance: (state, { payload }) => {
            state.routeDistance = payload;
        },

        setIsQuoteDetailsUpdated: (state, { payload }) => {
            state.isQuoteDetailsUpdated = payload;
        },
        setIsShowLinkThreadModal: (state, { payload }) => {
            state.isShowLinkThreadModal = payload;
        },

        setShowTranscriptPanel: (state, { payload }) => {
            state.showTranscriptPanel = payload;
        },

        setSelectedLeadData: (state, { payload }) => {
            state.selectedLeadData = payload;
        },

        setCommonCarriers: (state, { payload }) => {
            state.commonCarriers = payload;
        },

        setIsViewLoadPost: (state, { payload }) => {
            state.isViewLoadPost = payload;
        },

        setSelectedCommonCarrierData: (state, { payload }) => {
            state.selectedCommonCarrierData = payload;
        },

        setIsShowCommonCarriersOverviewModal: (state, { payload }) => {
            state.isShowCommonCarriersOverviewModal = payload;
        },

        setIsLoading: (state, { payload }) => {
            state.isLoading = payload;
        },

        setRateData: (state, { payload }) => {
            state.rateData = payload;
        },
        setRateCount: (state, { payload }) => {
            state.rateCount = payload;
        },
        setIsRatesLoading: (state, { payload }) => {
            state.isRatesLoading = payload;
        },

        setActiveQuoteColumns: (state, action: PayloadAction<Record<string, boolean> | null>) => {
            state.activeQuoteColumns = action.payload;
        },

        setActiveQuotesFetchedForId: (state, action: PayloadAction<string | number | null>) => {
            state.activeQuotesFetchedForId = action.payload;
        },

        setSelectedRateLoadPosting: (state, { payload }) => {
            state.selectedRateLoadPosting = payload;
            state.rateData = [];
            state.rateCount = 0;
        },

        setActivities: (state, { payload }) => {
            state.activities = payload;
        },

        appendActivities: (state, { payload }) => {
            state.activities = [...state.activities, ...payload];
        },

        prependActivity: (state, { payload }) => {
            state.activities = [payload, ...state.activities];
        },

        setActivitiesPagination: (state, { payload }) => {
            state.activitiesPagination = payload;
        },

        setActivitiesLoading: (state, { payload }) => {
            state.activitiesLoading = payload;
        },

        setActivitiesPostLoading: (state, { payload }) => {
            state.activitiesPostLoading = payload;
        },
    },
});

export default QuotingSlice.reducer;

export const {
    resetQuoteState,
    resetAllQuoteState,
    setQuote,
    setDimensions,
    setAddressDetails,
    setLoadLinkPosts,
    setServices,
    setIsShowQuoteDetailModal,
    setActiveTabQuote,
    setIsViewQuote,
    setActiveQuotes,
    updateRemoveActiveQuote,
    setCurrencyType,
    setRatesTableCurrency,
    setLoadPostingTableCurrency,
    setIsStatusWonOrLost,
    setIsOpenPostToLoadBoard,
    setLoadPostingDetail,
    setCustomerOverview,
    setIsRedirectedFromRateGatherAgent,
    setCommonCarrierRates,
    setSpotQuoteRates,
    setQuoteConnectedUsers,
    setPredictiveIndex,
    setIsQuoteDetailsUpdated,
    setRouteDistance,
    setIsShowLinkThreadModal,
    setShowTranscriptPanel,
    setSelectedLeadData,
    setCommonCarriers,
    setIsViewLoadPost,
    setSelectedCommonCarrierData,
    setIsShowCommonCarriersOverviewModal,
    setSelectedRateLoadPosting,
    updateActiveQuoteValue,
    setIsLoading,
    setRateData,
    setRateCount,
    setIsRatesLoading,
    setIsSpotQuoteLoadingWhenEditQuoteDetail,
    setActiveQuoteColumns,
    setActiveQuotesFetchedForId,
    setActivities,
    appendActivities,
    prependActivity,
    setActivitiesPagination,
    setActivitiesLoading,
    setActivitiesPostLoading,
} = QuotingSlice.actions;

export const quoteData = (state: RootState) => state.quoting.quote || {};

export const dimensionsData = (state: RootState) => state.quoting.dimensions || [];

export const addressDetailsData = (state: RootState) => state.quoting.addressDetails || {};

export const loadLinkPostsData = (state: RootState) => state.quoting.loadLinkPosts || [];

export const servicesData = (state: RootState) => state.quoting.services || [];

export const isShowQuoteDetailModalData = (state: RootState) => state.quoting.isShowQuoteDetailModal;

export const activeTabData = (state: RootState) => state.quoting.activeTab || 'address';

export const isViewQuoteData = (state: RootState) => state.quoting.isViewQuote;

export const activeQuotesData = (state: RootState) => state.quoting.activeQuotes || [];

export const currencyTypeData = (state: RootState) => state.quoting.currencyType || CURRENCY.CAD;

export const ratesTableCurrencyData = (state: RootState) => state.quoting.ratesTableCurrency || 'System';

export const loadPostingTableCurrencyData = (state: RootState) => state.quoting.loadPostingTableCurrency || CURRENCY.CAD;

export const isStatusWonOrLostData = (state: RootState) => state.quoting.isStatusWonOrLost || false;

export const isOpenPostToLoadBoardData = (state: RootState) => state.quoting.isOpenPostToLoadBoard || false;

export const loadPostingDetailData = (state: RootState) => state.quoting.loadPostingDetail || {};

export const customerOverviewData = (state: RootState) => state.quoting.customerOverview;

export const isRedirectedFromRateGatherAgentData = (state: RootState) => state.quoting.isRedirectedFromRateGatherAgent || false;

export const commonCarrierRatesData = (state: RootState) => state.quoting.commonCarrierRates || [];

export const spotQuoteRatesData = (state: RootState) => state.quoting.spotQuoteRates || [];

export const quoteConnectedUsersData = (state: RootState) => state.quoting.quoteConnectedUsers || [];

export const predictiveIndexData = (state: RootState) => state.quoting.predictiveIndex || {};

export const isQuoteDetailsUpdatedData = (state: RootState) => state.quoting.isQuoteDetailsUpdated;

export const routeDistanceData = (state: RootState) => state.quoting.routeDistance || 0;

export const isShowLinkThreadModalData = (state: RootState) => state.quoting.isShowLinkThreadModal;

export const showTranscriptPanelData = (state: RootState) => state.quoting.showTranscriptPanel || false;

export const selectedLeadDataData = (state: RootState) => state.quoting.selectedLeadData || null;

export const commonCarriersData = (state: RootState) => state.quoting.commonCarriers || [];

export const isViewLoadPostData = (state: RootState) => state.quoting.isViewLoadPost || false;

export const selectedCommonCarrierDataData = (state: RootState) => state.quoting.selectedCommonCarrierData || null;

export const isShowCommonCarriersOverviewModalData = (state: RootState) => state.quoting.isShowCommonCarriersOverviewModal || false;

export const selectedRateLoadPostingData = (state: RootState) => state.quoting.selectedRateLoadPosting as any | null;

export const isLoadingData = (state: RootState) => state.quoting.isLoading;

export const rateDataData = (state: RootState) => state.quoting.rateData || [];

export const rateCountData = (state: RootState) => state.quoting.rateCount || 0;

export const isRatesLoadingData = (state: RootState) => state.quoting.isRatesLoading || false;

export const activeQuoteColumnsData = (state: RootState) => state.quoting.activeQuoteColumns ?? null;

export const activeQuotesFetchedForIdData = (state: RootState) => state.quoting.activeQuotesFetchedForId ?? null;

export const activitiesData = (state: RootState) => state.quoting.activities || [];

export const activitiesPaginationData = (state: RootState) => state.quoting.activitiesPagination ?? null;

export const activitiesLoadingData = (state: RootState) => state.quoting.activitiesLoading;

export const activitiesPostLoadingData = (state: RootState) => state.quoting.activitiesPostLoading;
